/**
 * Canonical permission strings. One enum-like constant, consumed by:
 *   - server guards via @RequirePermission('<resource>:<action>')
 *   - web UI guards via the permissions[] array on GET /auth/me
 *
 * Source of truth: docs/adr/0001-access-control-matrix.md
 *
 * Naming: '<resource>:<action>' — lowercase, kebab-case for multi-word actions.
 */
export const Permission = {
  // Users
  USERS_CREATE: 'users:create',
  USERS_LIST: 'users:list',
  USERS_READ: 'users:read',
  USERS_UPDATE: 'users:update',
  USERS_RESET_PASSWORD: 'users:reset-password',
  USERS_DELETE: 'users:delete',

  // Clients
  CLIENTS_CREATE: 'clients:create',
  CLIENTS_UPDATE: 'clients:update',
  CLIENTS_DELETE: 'clients:delete',
  CLIENTS_LIST: 'clients:list',
  CLIENTS_READ: 'clients:read',

  // Projects
  PROJECTS_CREATE: 'projects:create',
  PROJECTS_UPDATE: 'projects:update',
  PROJECTS_DELETE: 'projects:delete',
  PROJECTS_LIST: 'projects:list',
  PROJECTS_READ: 'projects:read',
  PROJECTS_READ_FINANCIALS: 'projects:read-financials',
  PROJECTS_ASSIGN_WORKERS: 'projects:assign-workers',

  // Project planning / operations planner
  PROJECT_PLANNING_READ: 'project-planning:read',
  PROJECT_PLANNING_MANAGE: 'project-planning:manage',
  PROJECT_PLANNING_UPDATE_STATUS: 'project-planning:update-status',
  PROJECT_PLANNING_OVERRIDE: 'project-planning:override',

  // Project items (cabinets, panels, countertops, elements, lighting, hardware)
  PROJECT_ITEMS_CREATE: 'project-items:create',
  PROJECT_ITEMS_UPDATE: 'project-items:update',
  PROJECT_ITEMS_DELETE: 'project-items:delete',
  PROJECT_ITEMS_READ: 'project-items:read',

  // Catalog (reusable library)
  CATALOG_READ: 'catalog:read',
  CATALOG_CREATE: 'catalog:create',
  CATALOG_UPDATE: 'catalog:update',
  CATALOG_DELETE: 'catalog:delete',
  CATALOG_READ_COST: 'catalog:read-cost',
  // Access to the component-library Catalog sections (materials, edges, hardware,
  // lighting, drawer systems) — the tabs + their routes. Purely navigational:
  // reads/writes/pickers stay on the CATALOG_* perms above. Held by every role
  // except SALES_MANAGER, who works with those components only while building.
  CATALOG_COMPONENTS: 'catalog:components',

  // Analytics
  ANALYTICS_READ_OPERATIONAL: 'analytics:read-operational',
  ANALYTICS_READ_FINANCIAL: 'analytics:read-financial',
  ANALYTICS_READ_PERSONAL: 'analytics:read-personal',

  // Audit
  AUDIT_READ: 'audit:read',
  AUDIT_PRUNE: 'audit:prune',

  // Settings
  SETTINGS_READ: 'settings:read',
  SETTINGS_UPDATE: 'settings:update',

  // Rooms
  ROOMS_CREATE: 'rooms:create',
  ROOMS_UPDATE: 'rooms:update',
  ROOMS_DELETE: 'rooms:delete',
  ROOMS_READ: 'rooms:read',

  // Library
  LIBRARY_READ: 'library:read',
  LIBRARY_CREATE: 'library:create',
  LIBRARY_DELETE: 'library:delete',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];
